// import AlertsContext from 'common/contexts/alerts';
import useRouter from "common/hooks/use-router";
import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import React, {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { WalletIcon } from "@heroicons/react/24/outline";
import Header from "common/components/Header/Header";
import { JobPosition } from "types/JobPosition";

import FilteredList from "common/components/Table/FilteredList";
import useAuth from "common/hooks/useAuth";
import useOrganization from "common/hooks/useOrganization";
import checkRights from "common/utils/checkRights";
import getWording from "common/utils/wording";
import AlertsContext from "common/providers/alerts";
import JobPositionItem from "./JobPositionItem";

function JobPositions() {
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);

  const { user: currentUser } = useAuth();
  const { organization } = useOrganization();
  const canManageJobPositions = checkRights(
    currentUser,
    "canManageJobPositions",
    currentUser?.mySitesId,
    ["isAdmin"]
  );

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<JobPosition>("job-positions", {
    defaultFilters: organization?.id
      ? {
          organization: organization.id,
        }
      : {},
    defaultSort: "name:ASC",
    // populate: [],
    cachePrefix: `JobPositions_${organization?.id || "all"}`,
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  useEffect(() => {
    setFilter("organization", organization?.id || false);
  }, [organization, setFilter]);

  const { removeItem } = useItem<JobPosition>("job-positions", "");

  const { navigate } = useRouter();
  const handleNavigate = (id: any) => {
    navigate(`/job-positions/edit/${id}`);
  };
  // const { isAdmin } = useAuth();

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  /* const { setAlert } = useContext(AlertsContext);

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]); */

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$contains",
        filterLabel: t("filters.search"),
        columnClasses: "w-1/2 mb-2",
      },
      /* {
        Header: t("rights.toEditSite", {
                        wording: getWording(true),
                      }),
        headerClasses: "mb-3",
        filterName: "canEditSite",
        filterComponent: (props: any) => (
          <ToggleSwitch rightLabel={t("rights.canEditSite", {
                        wording: getWording(true),
                      })} {...props} />
        ),
        columnClasses: "w-1/3 mb-2",
      },
      {
        Header: t("rights.toManagePost"),
        headerClasses: "mb-3",
        filterName: "canManagePost",
        filterComponent: (props: any) => (
          <ToggleSwitch rightLabel={t("rights.canManagePost")} {...props} />
        ),
        columnClasses: "w-1/3 mb-2",
      },
      {
        Header: t("rights.toManageActivity"),
        headerClasses: "mb-3",
        filterName: "canManageActivity",
        filterComponent: (props: any) => (
          <ToggleSwitch rightLabel={t("rights.canManageActivity")} {...props} />
        ),
        columnClasses: "w-1/3",
      },
      {
        Header: t("rights.toManageContact"),
        headerClasses: "mb-3",
        filterName: "canManageContact",
        filterComponent: (props: any) => (
          <ToggleSwitch rightLabel={t("rights.canManageContact")} {...props} />
        ),
        columnClasses: "w-1/3",
      },
      {
        Header: t("rights.toAnswerMessage"),
        headerClasses: "mb-3",
        filterName: "canAnswerMessage",
        filterComponent: (props: any) => (
          <ToggleSwitch rightLabel={t("rights.canAnswerMessage")} {...props} />
        ),
        columnClasses: "w-1/3",
      }, */
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.jobPositions")}
        subtitle={t("titleLegends.jobPositions", { wording: getWording() })}
        btnTitle={t("newItem.jobPosition")}
        btnSubtitle={t("newItem.jobPositionLegend")}
        Icon={WalletIcon}
        addLink="/job-positions/add"
        addLinkDisabled={!canManageJobPositions}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={JobPositionItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default JobPositions;
